import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { useOntologyStore } from '@/stores/useOntologyStore';
import type { OntologyConcept, OntologyMetaType, OntologyStatus } from '@/types/ontology';

interface ConceptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domainId: string;
  concept?: OntologyConcept | null;
  token?: string | null;
  onSuccess?: () => void;
}

const META_TYPES: OntologyMetaType[] = [
  // Base
  'Entity', 'Process', 'Source',
  // Core Ontology v2
  'Actor', 'Spatial', 'Asset', 'Information',
  'Temporal', 'DataPoint', 'Quality', 'Situation', 'Rule', 'MasterData',
];
const STATUSES: OntologyStatus[] = ['CORE', 'APPROVED'];

export function ConceptDialog({ open, onOpenChange, domainId, concept, token, onSuccess }: ConceptDialogProps) {
  const { createConcept, updateConcept, loading } = useOntologyStore();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [metaType, setMetaType] = useState<OntologyMetaType>('Entity');
  const [status, setStatus] = useState<OntologyStatus>('APPROVED');
  const [error, setError] = useState<string | null>(null);

  const isEditMode = Boolean(concept);

  // Initialize form when concept changes
  useEffect(() => {
    if (concept) {
      setName(concept.name);
      setDescription(concept.description || '');
      setMetaType(concept.metaType);
      setStatus(concept.status);
    } else {
      setName('');
      setDescription('');
      setMetaType('Entity');
      setStatus('APPROVED');
    }
    setError(null);
  }, [concept, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      if (isEditMode && concept) {
        await updateConcept(concept.id, { name, description, metaType, status }, token);
      } else {
        await createConcept(domainId, { name, description, metaType, status }, token);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save concept');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Konzept bearbeiten' : 'Neues Konzept erstellen'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Bearbeite Name, Beschreibung, Metatyp und Status des Ontologie-Konzepts.'
              : 'Lege ein neues Ontologie-Konzept mit Name, Beschreibung, Metatyp und Status an.'}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">
              Name <span className="text-red-500">*</span>
            </label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="z.B. Kunde, Bestellposition"
              required
              disabled={loading || (isEditMode && concept?.status === 'CORE')}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">
              Beschreibung
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Kurze Beschreibung des Konzepts…"
              rows={3}
              disabled={loading}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">
              Meta Type <span className="text-red-500">*</span>
            </label>
            <Select value={metaType} onValueChange={(value) => setMetaType(value as OntologyMetaType)} disabled={loading}>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {META_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {type}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">
              Status <span className="text-red-500">*</span>
            </label>
            <Select value={status} onValueChange={(value) => setStatus(value as OntologyStatus)} disabled={loading || (isEditMode && concept?.status === 'CORE')}>
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isEditMode && concept?.status === 'CORE' && (
              <p className="text-[10px] text-muted-foreground mt-1">CORE-Konzepte sind schreibgeschützt</p>
            )}
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 text-destructive px-3 py-2 rounded text-xs">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={loading || (isEditMode && concept?.status === 'CORE')}>
              {loading && <Loader2 size={14} className="animate-spin mr-1" />}
              {isEditMode ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
